// MixedFamilyRationalFractionBarToSinglePowerTask.js
// Zadanie 6 – „zadanie 5” w liczniku i mianowniku (2–3 czynniki na górze i dole, z dwukropkami)

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const EQUIV = {
  4: { base: 2, power: 2 },
  8: { base: 2, power: 3 },
  9: { base: 3, power: 2 },
  27: { base: 3, power: 3 },
  25: { base: 5, power: 2 },
  125: { base: 5, power: 3 },
};

const FAMILY = {
  2: { nums: [2, 4, 8], mul: { 2: 1, 4: 2, 8: 3 } },
  3: { nums: [3, 9, 27], mul: { 3: 1, 9: 2, 27: 3 } },
  5: { nums: [5, 25, 125], mul: { 5: 1, 25: 2, 125: 3 } },
};

const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = a % b;
    a = b;
    b = t;
  }
  return a || 1;
};
const lcm = (a, b) => Math.abs(a * b) / gcd(a, b);

function normFrac(n, d) {
  if (d === 0) return { n: 0, d: 1 };
  const s = d < 0 ? -1 : 1;
  const g = gcd(n, d);
  return { n: s * (n / g), d: s * (d / g) };
}

function addFrac(a, b) {
  const D = lcm(a.d, b.d);
  const n = a.n * (D / a.d) + b.n * (D / b.d);
  return normFrac(n, D);
}

const simplifyFraction = (n, d) => normFrac(n, d);

const expLatex = (n, d = 1) => {
  const { n: nn, d: dd } = simplifyFraction(n, d);
  const sign = nn < 0 ? "-" : "";
  const a = Math.abs(nn);
  if (a % dd === 0) return `${sign}${a / dd}`;
  return `${sign}\\tfrac{${a}}{${dd}}`;
};

// czy po skróceniu wykładnik to dokładnie 1
const isExponentOne = (n, d = 1) => {
  const { n: nn, d: dd } = simplifyFraction(n, d);
  return nn === dd; // 1/1
};

/* --- generatory pojedynczych składników (jak w zadaniu 5) --- */

function radicalTerm(famBase) {
  const fam = FAMILY[famBase];
  const num = pick(fam.nums);
  const mul = fam.mul[num];
  const q = pick([2, 3, 4, 5]);
  const p = pick([1, 2, 3]);

  const contrib = normFrac(mul * p, q);
  const inside = p === 1 ? `${num}` : `${num}^{${p}}`;
  const latex = q === 2 ? `\\sqrt{${inside}}` : `\\sqrt[${q}]{${inside}}`;
  return { latex, contrib, meta: { kind: "rad", q, num, p, mul } };
}

function radicalFractionTerm(famBase) {
  const fam = FAMILY[famBase];
  const num = pick(fam.nums);
  const mul = fam.mul[num];
  const q = pick([2, 3, 4, 5]);
  const p = pick([1, 2, 3]);

  const contrib = normFrac(-mul * p, q);
  const pow = `${num}^{-${p}}`;
  const latex =
    q === 2 ? `\\sqrt{${pow}}` : `\\sqrt[${q}]{${pow}}`;

  return { latex, contrib, meta: { kind: "radFrac", q, num, p, mul } };
}

function rationalPowerTerm(famBase, { forceNegative = false } = {}) {
  const fam = FAMILY[famBase];
  const num = pick(fam.nums);
  const mul = fam.mul[num];

  const sRaw = pick([2, 3, 4, 5]);
  const rRaw = pick([1, 2, 3, 4]);
  const sign = forceNegative ? -1 : Math.random() < 0.5 ? -1 : 1;

  const frac = normFrac(sign * rRaw, sRaw);
  const n = frac.n;
  const d = frac.d;
  const absN = Math.abs(n);

  const contrib = normFrac(mul * n, d);

  let latex;
  if (absN % d === 0) {
    const k = absN / d;
    latex = n < 0 ? `${num}^{-${k}}` : `${num}^{${k}}`;
  } else {
    latex =
      n < 0
        ? `${num}^{-\\tfrac{${absN}}{${d}}}`
        : `${num}^{\\tfrac{${absN}}{${d}}}`;
  }

  return {
    latex,
    contrib,
    meta: { kind: "rat", num, mul, r: n, s: d, sign: n < 0 ? -1 : 1 },
  };
}


function integerPowerOrFraction(famBase) {
  const fam = FAMILY[famBase];
  const num = pick(fam.nums);
  const mul = fam.mul[num];

  const k = pick([2, 3, 4, 5]);
  const variant = pick(["frac", "pow", "divide"]);

  if (variant === "frac") {
    const latex = `\\dfrac{1}{${num}^{${k}}}`;
    const contrib = normFrac(-mul * k, 1);
    return { latex, contrib, as: "frac", meta: { kind: "frac", num, k, mul } };
  }
  if (variant === "divide") {
    const latex = `${num}^{${k}}`;
    const contrib = normFrac(-mul * k, 1);
    return {
      latex,
      contrib,
      as: "divide",
      meta: { kind: "divide", num, k, mul },
    };
  }
  const latex = `${num}^{${k}}`;
  const contrib = normFrac(mul * k, 1);
  return { latex, contrib, as: "pow", meta: { kind: "pow", num, k, mul } };
}

function generateSidePieces(famBase) {
  // dokładnie jak zadanie 5: pierwiastek z ułamka + coś + coś
  const pieces = [];
  pieces.push(radicalFractionTerm(famBase));
  pieces.push(
    Math.random() < 0.5 ? radicalTerm(famBase) : rationalPowerTerm(famBase)
  );
  pieces.push(integerPowerOrFraction(famBase));
  return pieces;
}

/* ---------- Generator zadania ---------- */

export function generateTask() {
  const famBase = pick([2, 3, 5]);

  let best = null;

  for (let tries = 0; tries < 100; tries++) {
    let numPieces = generateSidePieces(famBase);
    let denPieces = generateSidePieces(famBase);

    // chcemy łącznie 4 lub 5 składników: 2+2, 2+3, 3+2
    const patterns = [
      { num: 2, den: 2 },
      { num: 2, den: 3 },
      { num: 3, den: 2 },
      { num: 2, den: 3 },
      { num: 3, den: 2 },
    ];
    const pat = pick(patterns);

    while (numPieces.length > pat.num) numPieces.pop();
    while (denPieces.length > pat.den) denPieces.pop();

    let K = normFrac(0, 1);
    numPieces.forEach((p) => {
      K = addFrac(K, p.contrib);
    });
    denPieces.forEach((p) => {
      K = addFrac(K, normFrac(-p.contrib.n, p.contrib.d));
    });

    if (K.n < 0 && K.n % K.d !== 0) {
      best = { numPieces, denPieces, K };
      break;
    }
    if (!best && K.n < 0) {
      best = { numPieces, denPieces, K };
    }
  }

  if (!best) {
    const numPieces = generateSidePieces(famBase).slice(0, 2);
    const denPieces = generateSidePieces(famBase).slice(0, 2);
    let K = normFrac(0, 1);
    numPieces.forEach((p) => {
      K = addFrac(K, p.contrib);
    });
    denPieces.forEach((p) => {
      K = addFrac(K, normFrac(-p.contrib.n, p.contrib.d));
    });
    best = { numPieces, denPieces, K };
  }

  const { numPieces, denPieces, K } = best;

  const numLatex = numPieces
    .map((p, i) =>
      i === 0
        ? p.latex
        : (p.as === "divide" ? "\\,:\\," : "\\,\\cdot\\,") + p.latex
    )
    .join("");
  const denLatex = denPieces
    .map((p, i) =>
      i === 0
        ? p.latex
        : (p.as === "divide" ? "\\,:\\," : "\\,\\cdot\\,") + p.latex
    )
    .join("");

  const leftLatex = `\\dfrac{${numLatex}}{${denLatex}}`;

  return {
    type: "MixedFamilyRationalFractionBarToSinglePowerTask",
    data: { base: famBase, leftLatex, K, numPieces, denPieces },
  };
}

/* ---------- Render / Input ---------- */

export function renderTask({ data }) {
  const { base, leftLatex } = data;
  return (
    <div style={{ marginBottom: "12px" }}>
      <div>
        Przedstaw w postaci jednej potęgi o podstawie{" "}
        <InlineMath math={String(base)} />:
      </div>
      <BlockMath math={leftLatex} />
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const placeholder = "np. 5^2/3 lub 5^-2/3";

  return (
    <div>
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        label=""
        powerPreview={true}
      />
      <div aria-hidden style={{ marginTop: "-16px" }} />
      <div aria-hidden style={{ height: "12px" }} />
    </div>
  );
}

/* ---------- Parsowanie / sprawdzanie – jak w zadaniu 5 ---------- */

function parseUser(raw) {
  if (!raw) return null;
  const s = String(raw).replace(/\s/g, "");

  let m = s.match(/^1\/\((\d+)\^\((-?\d+)\/(\d+)\)\)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const num = -parseInt(m[2], 10);
    const den = parseInt(m[3], 10);
    return { base, exp: normFrac(num, den) };
  }

  m = s.match(/^1\/(\d+)\^\((-?\d+)\/(\d+)\)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const num = -parseInt(m[2], 10);
    const den = parseInt(m[3], 10);
    return { base, exp: normFrac(num, den) };
  }

  m = s.match(/^(\d+)\^\((-?\d+)\/(\d+)\)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const num = parseInt(m[2], 10);
    const den = parseInt(m[3], 10);
    return { base, exp: normFrac(num, den) };
  }

  m = s.match(/^(\d+)\^\-?(\d+)\/(\d+)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const hasMinus = s.includes("^-");
    const num = hasMinus ? -parseInt(m[2], 10) : parseInt(m[2], 10);
    const den = parseInt(m[3], 10);
    return { base, exp: normFrac(num, den) };
  }

  m = s.match(/^1\/(\d+)\^(\d+)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const k = parseInt(m[2], 10);
    return { base, exp: normFrac(-k, 1) };
  }

  m = s.match(/^(\d+)\^\-?(\d+)$/);
  if (m) {
    const base = parseInt(m[1], 10);
    const k = parseInt(m[2], 10);
    const isNeg = s.includes("^-");
    return { base, exp: normFrac(isNeg ? -k : k, 1) };
  }

  return null;
}

function sameFrac(a, b) {
  return a.n * b.d === b.n * a.d;
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw)
    return false;
  const parsed = parseUser(userAnswer.raw);
  if (!parsed) return false;

  let base = parsed.base;
  let n = parsed.exp.n;
  let d = parsed.exp.d;

  const eq = EQUIV[base];
  if (eq) {
    base = eq.base;
    n = n * eq.power;
  }

  if (base !== data.base) return false;

  const target = simplifyFraction(data.K.n, data.K.d);
  const userExp = simplifyFraction(n, d);

  return sameFrac(target, userExp);
}

/* ---------- Wyświetlanie odpowiedzi ---------- */

function prettyUserLatex(raw) {
  if (!raw) return "";
  const s = String(raw).replace(/\s/g, "");

  let m = s.match(/^1\/\((\d+)\^\((-?\d+)\/(\d+)\)\)$/);
  if (m)
    return `\\dfrac{1}{${m[1]}^{\\left(${expLatex(
      -parseInt(m[2], 10),
      parseInt(m[3], 10)
    )}\\right)}}`;

  m = s.match(/^1\/(\d+)\^\((-?\d+)\/(\d+)\)$/);
  if (m)
    return `\\dfrac{1}{${m[1]}^{${expLatex(
      -parseInt(m[2], 10),
      parseInt(m[3], 10)
    )}}}`;

  m = s.match(/^(\d+)\^\((-?\d+)\/(\d+)\)$/);
  if (m)
    return `${m[1]}^{${expLatex(
      parseInt(m[2], 10),
      parseInt(m[3], 10)
    )}}`;

  m = s.match(/^(\d+)\^\-?(\d+)\/(\d+)$/);
  if (m) {
    const num = s.includes("^-") ? -parseInt(m[2], 10) : parseInt(m[2], 10);
    return `${m[1]}^{${expLatex(num, parseInt(m[3], 10))}}`;
  }

  m = s.match(/^1\/(\d+)\^(\d+)$/);
  if (m) return `\\dfrac{1}{${m[1]}^{${m[2]}}}`;

  m = s.match(/^(\d+)\^\-?(\d+)$/);
  if (m) {
    const isNeg = s.includes("^-");
    return `${m[1]}^{${isNeg ? "-" : ""}${m[2]}}`;
  }

  return s;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw)
    return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={prettyUserLatex(userAnswer.raw)} />;
}

export function renderAnswer({ data }) {
  const { base, K } = data;
  const e = expLatex(K.n, K.d);
  const one = isExponentOne(K.n, K.d);
  return <InlineMath math={one ? String(base) : `${base}^{${e}}`} />;
}

/* ---------- Wyjaśnienie ---------- */

export function renderExplanation({ data }) {
  const { base, leftLatex, K, numPieces, denPieces } = data;

  const hint =
    base === 2
      ? "4=2^{2},\\;8=2^{3}"
      : base === 3
      ? "9=3^{2},\\;27=3^{3}"
      : "25=5^{2},\\;125=5^{3}";

  const rules =
    "a^{r}\\cdot a^{s}=a^{r+s}\\quad (a^{r})^{t}=a^{r\\cdot t}\\quad \\sqrt[q]{a^{p}}=a^{\\tfrac{p}{q}}\\quad \\dfrac{1}{a^{r}}=a^{-r}";

  const sumSide = (pieces) => {
    const exps = pieces.map((t) => expLatex(t.contrib.n, t.contrib.d));
    const str = exps.reduce((acc, e, i) => {
      if (i === 0) return e;
      return e.startsWith("-") ? `${acc} ${e}` : `${acc} +${e}`;
    }, "");
    const tot = pieces.reduce(
      (acc, t) => addFrac(acc, t.contrib),
      normFrac(0, 1)
    );
    return { str, tot: expLatex(tot.n, tot.d) };
  };

  const num = sumSide(numPieces);
  const den = sumSide(denPieces);
  const finalExp = expLatex(K.n, K.d);
  const one = isExponentOne(K.n, K.d);
  const rightMost = one ? `${base}` : `${base}^{${finalExp}}`;

  return (
    <div style={{ display: "grid", rowGap: "8px" }}>
      <p>
        <strong>Krok 1:</strong> Zamień liczby z rodziny na potęgi o wspólnej
        podstawie {base}.
      </p>
      <BlockMath math={hint} />

      <p>
        <strong>Krok 2:</strong> Zsumuj wykładniki w
        liczniku.
      </p>
      <BlockMath
        math={`${base}^{${num.str}}\\;=\\;${base}^{${num.tot}}`}
      />

      <p>
        <strong>Krok 3:</strong> Zsumuj wykładniki w mianowniku.
      </p>
      <BlockMath
        math={`${base}^{${den.str}}\\;=\\;${base}^{${den.tot}}`}
      />

      <p>
        <strong>Krok 4:</strong> Zastosuj zasadę{" "}
        <InlineMath math={"a^{r}:a^{s}=a^{r-s}"} />.
      </p>
      <BlockMath
        math={`\\dfrac{${base}^{${num.tot}}}{${base}^{${den.tot}}}\\;=\\;${base}^{${num.tot}-${den.tot}}\\;=\\;${rightMost}`}
      />

      <p>
        (Całe wyrażenie:)
      </p>
      <BlockMath math={leftLatex} />
      <p>
        sprowadziliśmy do jednej potęgi o podstawie{" "}
        <InlineMath math={String(base)} />.
      </p>
    </div>
  );
}
